import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];

const EXAMPLES = [
  { id: "heated-argument", emoji: "😤", label: "Heated argument" },
  { id: "silent-treatment", emoji: "😶", label: "Silent treatment" },
  { id: "money-fight", emoji: "💸", label: "Money fight" },
];

export function LandingPage() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const validateAndGo = (file: File | undefined) => {
    if (!file) return;
    setFileError(null);

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setFileError("Please select a PNG, JPG, GIF, or WebP image.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setFileError("Image must be under 10 MB.");
      return;
    }
    navigate("/analyze/results", { state: { file } });
  };

  const openFilePicker = () => inputRef.current?.click();

  const loadExample = (id: string) => {
    navigate(`/analyze/demo/${id}`);
  };

  return (
    <div className="min-h-dvh bg-white flex flex-col">
      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        className="hidden"
        onChange={(e) => validateAndGo(e.target.files?.[0])}
      />

      {/* ── Scrollable content ── */}
      <div className="flex-1 overflow-y-auto">

      {/* ── Nav ── */}
      <nav className="flex items-center justify-between px-6 md:px-12 py-6 border-b border-[#eee]">
        <span className="font-serif text-2xl text-[#1a1a1a]">Wren</span>
        <div className="flex items-center gap-6">
          <a href="https://chatwren.com" className="text-sm text-[#4a4a4a] hidden sm:block">
            ← Back to Wren
          </a>
        </div>
      </nav>

      <div className="max-w-xl mx-auto px-6 pb-16">

        {/* ── Hero ── */}
        <section className="text-center pt-6 pb-4 sm:pt-16 sm:pb-12">
          <span className="hidden sm:inline-block text-xs font-medium text-[#435843] bg-[#d6ddd3] px-4 py-2 rounded-full mb-8">
            🔍 Free tool by Wren
          </span>
          <h1 className="font-serif text-xl sm:text-4xl md:text-[48px] text-[#1a1a1a] leading-[1.1] mb-2 sm:mb-6">
            What&apos;s really happening in your texts?
          </h1>
          <p className="text-sm sm:text-[17px] text-[#4a4a4a] leading-[1.5] sm:leading-[1.7] max-w-md mx-auto">
            Upload a screenshot and get an instant AI breakdown.
            <span className="hidden sm:inline"> Wren analyzes the communication patterns, emotional dynamics, and hidden signals.</span>
          </p>
        </section>

        {/* ── Upload Card ── */}
        <section className="bg-white border border-[#e0dcd6] rounded-[20px] p-6 sm:p-8 md:p-12 mb-16">
          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); validateAndGo(e.dataTransfer.files[0]); }}
            onClick={openFilePicker}
            className={`rounded-2xl border-2 border-dashed p-6 sm:p-12 flex flex-col items-center cursor-pointer transition-colors ${
              dragOver
                ? "border-[#435843] bg-[#d6ddd3]/20"
                : "border-[#e0dcd6] bg-[#fafaf7]"
            }`}
          >
            <span className="text-3xl sm:text-4xl mb-2 sm:mb-4">📱</span>
            <p className="font-serif text-lg sm:text-xl text-[#1a1a1a] mb-1 sm:mb-2">
              Drop your screenshot here
            </p>
            <p className="text-xs text-[#888] text-center leading-relaxed mb-4 sm:mb-6">
              Any text message screenshot · PNG, JPG, GIF, or WebP
            </p>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); openFilePicker(); }}
              className="bg-[#435843] text-white text-sm font-bold px-8 py-4 rounded-lg"
            >
              Choose screenshot
            </button>
          </div>

          {fileError && (
            <p className="text-xs text-red-500 mt-4 text-center">{fileError}</p>
          )}

          {/* Privacy notes */}
          <div className="hidden sm:flex flex-col gap-2 mt-6">
            <p className="text-xs text-[#888] text-center">
              🔒 Your messages are encrypted in transit and at rest
            </p>
            <p className="text-xs text-[#888] text-center">
              🚫 Your data doesn&apos;t train AI — zero-retention with all providers
            </p>
            <p className="text-xs text-[#888] text-center">
              ✅ HIPAA-compliant infrastructure with regular security audits
            </p>
          </div>
          <p className="sm:hidden text-xs text-[#888] text-center mt-4">
            🔒 Private &amp; encrypted · No AI training · HIPAA-compliant
          </p>

          {/* Divider */}
          <div className="flex items-center gap-4 my-4 sm:my-6">
            <div className="flex-1 border-t border-[#e0dcd6]" />
            <span className="text-xs text-[#888]">or try an example</span>
            <div className="flex-1 border-t border-[#e0dcd6]" />
          </div>

          {/* Example question */}
          <p className="hidden sm:block text-xs text-[#888] text-center mb-4">
            Don&apos;t have a screenshot handy?
          </p>

          {/* Example buttons */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            {EXAMPLES.map((ex) => (
              <button
                key={ex.label}
                type="button"
                onClick={() => loadExample(ex.id)}
                className="text-xs text-[#4a4a4a] border border-[#e0dcd6] bg-white px-4 py-2 rounded-full hover:bg-[#fafaf7] transition-colors"
              >
                {ex.emoji} {ex.label}
              </button>
            ))}
          </div>
        </section>

        {/* ── Example Result ── */}
        <section className="mb-16">
          <p className="text-xs text-[#888] uppercase tracking-[1.5px] text-center mb-6">
            Example result
          </p>

          {/* Top card: Analysis preview */}
          <div className="bg-[#f8f8f6] rounded-t-[20px] p-8 md:p-8">
            {/* Header */}
            <div className="border-b border-[#e0dcd6] pb-6 mb-8 text-center">
              <h2 className="font-serif text-2xl text-[#1a1a1a]">
                Your Conversation Breakdown
              </h2>
              <p className="text-xs text-[#888] mt-1">
                Based on 6 messages analyzed
              </p>
            </div>

            {/* About card preview */}
            <div className="rounded-[20px] shadow-[0_1px_10px_rgba(0,0,0,0.1)] overflow-hidden">
              {/* On the Surface */}
              <div className="bg-white px-6 pt-6 pb-4">
                <p className="text-xs font-bold text-[#888] uppercase tracking-[1px] mb-4">
                  On the surface
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-6 h-6 rounded-full bg-[#eff0ee] flex items-center justify-center flex-shrink-0">
                    <span className="text-xs">🛏️</span>
                  </div>
                  <span className="text-base font-semibold text-[#4a4a4a]">
                    Emma&apos;s bedtime
                  </span>
                </div>
              </div>

              {/* But Really divider */}
              <div className="relative h-[22px] flex items-center justify-center bg-gradient-to-b from-white via-[#e8e5df] to-white">
                <span className="relative z-10 text-[9px] font-bold text-white bg-[#5c6b55] px-4 py-1 rounded-full uppercase tracking-[1px]">
                  But really
                </span>
              </div>

              {/* What's Actually Felt */}
              <div className="bg-white px-6 pt-4 pb-6">
                <p className="text-xs font-bold text-[#888] uppercase tracking-[1px] mb-4">
                  What&apos;s actually felt
                </p>
                <div className="flex flex-wrap gap-2">
                  {["Exhaustion", "Loneliness", "Invisible"].map((f) => (
                    <span
                      key={f}
                      className="text-xs font-medium text-[#5c6b55] bg-[rgba(92,107,85,0.1)] px-2 py-1 rounded-full"
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </div>

              {/* What's Really Being Asked */}
              <div className="bg-[#2a2a2a] px-6 py-6">
                <p className="text-xs font-bold text-white uppercase tracking-[1px] mb-4">
                  What&apos;s really being asked
                </p>
                <p className="text-xs font-bold text-[#c5ccc2] leading-relaxed">
                  &ldquo;Do you see how hard I&apos;m trying?&rdquo;
                </p>
                <p className="text-xs text-white leading-relaxed mt-1">
                  They need to feel like their effort is witnessed and that someone
                  knows what bedtime alone every night actually costs.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom card: Coaching preview (blurred) */}
          <div className="relative rounded-b-[20px] overflow-hidden" style={{ height: 320 }}>
            {/* Coaching cards behind blur */}
            <div className="absolute inset-0 bg-[#f8f8f6] px-6 pt-6">
              <p className="text-xs font-bold text-[#5c6b55] uppercase tracking-[1.5px] mb-6">
                What to try next time
              </p>

              {/* Card 1: Morgan */}
              <div className="bg-white rounded-[20px] shadow-[0_1px_10px_rgba(0,0,0,0.1)] p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-xl bg-[#7c8fbf] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    M
                  </div>
                  <span className="text-xs font-semibold text-[#1a1a1a]">Morgan</span>
                  <span className="ml-auto text-[9px] font-bold text-[#5b21b6] bg-[#ede9fe] px-2 py-0.5 rounded uppercase tracking-[0.5px]">
                    Ask Better
                  </span>
                </div>
                <p className="text-xs text-[#4a4a4a] leading-relaxed mb-2">
                  The feeling is valid but the delivery buries it. When the exhaustion
                  hits, try leading with the need instead of the blame.
                </p>
                <div className="bg-[#f8f7f4] rounded-lg px-4 py-2">
                  <p className="text-xs text-[#1a1a1a] italic leading-relaxed">
                    &ldquo;I hear you on bedtime. Honestly, I&apos;ve been doing it alone and
                    it&apos;s been really hard. I need your help figuring this out.&rdquo;
                  </p>
                </div>
              </div>

              {/* Card 2: Chris */}
              <div className="bg-white rounded-[20px] shadow-[0_1px_10px_rgba(0,0,0,0.1)] p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-xl bg-[#bf7c9f] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    C
                  </div>
                  <span className="text-xs font-semibold text-[#1a1a1a]">Chris</span>
                  <span className="ml-auto text-[9px] font-bold text-[#1e40af] bg-[#dbeafe] px-2 py-0.5 rounded uppercase tracking-[0.5px]">
                    Listen Better
                  </span>
                </div>
                <p className="text-xs text-[#4a4a4a] leading-relaxed mb-2">
                  Morgan&apos;s jab landed hard, and the instinct to defend is natural.
                  But underneath the attack is someone saying &ldquo;I&apos;m tired and alone
                  in this.&rdquo; Try getting curious before getting defensive.
                </p>
                <div className="bg-[#f8f7f4] rounded-lg px-4 py-2">
                  <p className="text-xs text-[#1a1a1a] italic leading-relaxed">
                    &ldquo;That sounds really frustrating. I didn&apos;t realize how much
                    you&apos;ve been handling on your own. What have evenings been like?&rdquo;
                  </p>
                </div>
              </div>
            </div>

            {/* Blur overlay */}
            <div className="absolute inset-0 backdrop-blur-[4.4px] bg-[rgba(216,216,216,0.8)]" />

            {/* CTA on top of blur */}
            <div className="absolute inset-0 flex flex-col items-center justify-center px-8">
              <h3 className="font-serif text-xl text-[#1a1a1a] text-center mb-2">
                See your full report in 30 seconds
              </h3>
              <p className="text-xs text-[#888] text-center mb-6">
                Upload a screenshot to get your personalized breakdown
              </p>
              <button
                type="button"
                onClick={openFilePicker}
                className="bg-[#435843] text-white text-base font-bold px-8 py-4 rounded-lg mb-4"
              >
                Upload screenshot →
              </button>
              <p className="text-xs text-[#888]">
                🔒 Results expire after 10 minutes
              </p>
            </div>
          </div>
        </section>

        {/* ── Green Upsell Card ── */}
        <section className="pb-16">
          <div className="bg-[#435843] rounded-[20px] px-6 md:px-8 py-12 text-center">
            <p className="text-xs text-white/70 uppercase tracking-[1.5px] mb-4">
              Want this insight in real time?
            </p>
            <h2 className="font-serif text-2xl md:text-[32px] text-white leading-[1.35] mb-4">
              Wren does this automatically,<br />
              inside your texts
            </h2>
            <p className="text-base text-white/85 leading-relaxed mb-6">
              No screenshots needed. Wren lives in your iMessage and
              coaches you through tense moments as they happen.
            </p>

            {/* Feature checkmarks */}
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-4 mb-8">
              {["Real-time suggestions", "Weekly report card", "Private coaching"].map((feat) => (
                <div key={feat} className="flex items-center gap-1 opacity-80">
                  <div className="w-5 h-5 rounded-[10px] bg-white/20 flex items-center justify-center">
                    <span className="text-xs text-white">✓</span>
                  </div>
                  <span className="text-xs text-white">{feat}</span>
                </div>
              ))}
            </div>

          </div>
        </section>
      </div>
      </div>
    </div>
  );
}
